import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Zap, Phone, Mail, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, areas } from "./data";

const policyText = {
  privacy:
    "We only collect the details you choose to send us through the enquiry form — your name, phone number, email address and message — and we use them solely to respond to your enquiry. We never share, sell or use your details for marketing, and they're handled in line with UK GDPR. You can ask us to delete your details at any time by emailing hello@essexsparkelectrical.co.uk.",
  terms:
    "Quotes are provided free of charge and without obligation. All work is agreed in writing before it begins, and carried out in line with current BS 7671 wiring regulations. Payment terms are confirmed with your written quote. If anything isn't right, contact us first and we'll put it right.",
};

const Footer = () => {
  const [modal, setModal] = useState(null);

  return (
    <footer data-testid="site-footer" className="bg-slate-950 text-slate-300 pb-28 md:pb-0">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 bg-blue-600 rounded-md flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-400" fill="currentColor" />
            </span>
            <span className="font-heading leading-none">
              <span className="block text-lg font-bold tracking-tight text-white">Essex Spark</span>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Electrical</span>
            </span>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            Reliable electrical services for homes, landlords and local
            businesses across Basildon and Essex.
          </p>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white">Contact</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a data-testid="footer-phone-link" href={PHONE_TEL} className="inline-flex items-center gap-2 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-sm">
                <Phone className="w-4 h-4 text-amber-400" /> {PHONE_DISPLAY}
              </a>
            </li>
            <li>
              <a data-testid="footer-email-link" href={`mailto:${EMAIL}`} className="inline-flex items-center gap-2 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-sm break-all">
                <Mail className="w-4 h-4 text-amber-400 shrink-0" /> {EMAIL}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white">Service areas</h3>
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            {areas.join(" · ")} and surrounding Essex areas
          </p>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-white">Opening hours</h3>
          <ul className="mt-5 space-y-2 text-sm text-slate-400">
            <li className="flex justify-between gap-6"><span>Mon – Fri</span><span className="text-slate-200">8am – 6pm</span></li>
            <li className="flex justify-between gap-6"><span>Saturday</span><span className="text-slate-200">9am – 1pm</span></li>
            <li className="flex justify-between gap-6"><span>Sunday</span><span className="text-slate-200">Closed</span></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <p className="text-xs text-slate-500 flex-1" data-testid="footer-copyright">
            © 2026 Essex Spark Electrical. All rights reserved.
            <span className="block mt-1.5 text-[11px] text-slate-600" data-testid="footer-demo-note">
              Demo website — business details and testimonials shown for demonstration purposes only.
            </span>
          </p>
          <div className="flex gap-6">
            <button
              data-testid="footer-privacy-link"
              onClick={() => setModal("privacy")}
              className="text-xs font-semibold text-slate-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-sm"
            >
              Privacy Policy
            </button>
            <button
              data-testid="footer-terms-link"
              onClick={() => setModal("terms")}
              className="text-xs font-semibold text-slate-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 rounded-sm"
            >
              Terms
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {modal && (
          <motion.div
            data-testid="footer-policy-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-slate-950/70 flex items-center justify-center p-5"
            onClick={() => setModal(null)}
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white text-slate-900 rounded-md max-w-lg w-full p-8"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-bold">
                  {modal === "privacy" ? "Privacy Policy" : "Terms of Service"}
                </h3>
                <button
                  data-testid="footer-policy-modal-close"
                  onClick={() => setModal(null)}
                  className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{policyText[modal]}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
